import { Trash } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { SVG_SIZE_M } from 'src/constants';
import { Button } from 'src/v2/commonComponents/Button';
import {
  useComparingConversationsParams,
  useDeleteMessage,
  useVisible,
} from 'src/hooks';
import { CommonDeleteModal } from 'src/components/CommonDeleteModal';
import { useContext, useMemo } from 'react';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import { Message, MessageType } from 'src/types';
import { getTaskIdFromMessage } from 'src/utils';

const MESSAGES_COUNT_PER_ONE_TASK = 2;

type DeleteMessageButtonProps = {
  message: Message;
  disabled?: boolean;
  withTitle?: boolean;
  onClick?: () => void;
};

export const DeleteMessageButton = ({
  message,
  disabled,
  withTitle,
  onClick,
}: DeleteMessageButtonProps) => {
  const {
    isVisible: isDeleteModalVisible,
    onVisibilitySet: onDeleteModalVisibilitySet,
    onVisibilityRemove: onDeleteModalVisibilityRemove,
  } = useVisible();

  const { isCompareMode } = useComparingConversationsParams();
  const { deleteMessageById, isDeleteLoading } = useDeleteMessage();

  const { mainMessageListRef } = useContext(ForwardRefContext);
  const taskId = getTaskIdFromMessage(message);

  const onDeleteMessage = async () => {
    if (
      !message.message_id ||
      !message.conversation_id ||
      (message?.message_type !== MessageType.ERROR_MESSAGE && !taskId)
    ) {
      return null;
    }

    const messages = mainMessageListRef?.current?.data.get();

    const shouldRemoveConversation =
      messages?.length === MESSAGES_COUNT_PER_ONE_TASK;
    const currentMessageIndex = messages?.findIndex(
      ({ message_id }) => message_id === message.message_id,
    );
    const userMessageId = currentMessageIndex
      ? messages?.[currentMessageIndex - 1]?.message_id
      : '';

    if (!userMessageId) {
      return null;
    }

    await deleteMessageById({
      message_ids: [message.message_id, userMessageId],
      conversation_id: message.conversation_id,
      task_id: taskId,
      shouldRemoveConversation,
    });

    toast.success('Message request and response deleted successfully');
  };

  const isDisabled = useMemo(
    () => disabled || !message?.round_id,
    [disabled, message?.round_id],
  );

  const handleCloseDeleteModal = () => {
    onClick?.();
    onDeleteModalVisibilityRemove();
  };

  if (isCompareMode) {
    return null;
  }

  return (
    <>
      <CommonDeleteModal
        isOpen={isDeleteModalVisible}
        onClose={handleCloseDeleteModal}
        onSubmit={onDeleteMessage}
        isLoading={isDeleteLoading}
        title="Delete message"
        customBody={
          <p>
            Are you sure you want to delete the message? This action cannot be
            undone.
          </p>
        }
      />

      <Button
        color="transparent"
        onClick={onDeleteModalVisibilitySet}
        disabled={isDisabled}
        data-e2e="delete-message-button"
        tooltipContent="Delete message"
        tooltipPlace="left"
        size={withTitle ? 'medium' : 'small'}
        shape={withTitle ? 'regular' : 'round'}
        fullWidthWithLeftAlign={withTitle}
      >
        <Trash size={SVG_SIZE_M} color="currentColor" />
        {withTitle && 'Delete message'}
      </Button>
    </>
  );
};
